import { useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from '@refinedev/core';
import {
  ActionIcon,
  Menu,
  Modal,
  Loader,
  AspectRatio,
  ScrollArea,
  Table,
  Text,
} from '@mantine/core';
import {
  IconDotsVertical,
  IconDownload,
  IconEye,
  IconListDetails,
} from '@tabler/icons-react';
import { useDownloadFromS3 } from 'hooks';
import { generateGetSignedUrl } from 'api/action/s3';
import type { MRT_Row } from 'mantine-react-table';
import type { Remittance, RemittanceLine } from 'interfaces';
import * as Sentry from '@sentry/react';
import { useGetRemittance } from 'api/resource/remittance';

interface RemittanceRowActionsProps {
  row: MRT_Row<Remittance>;
  canModify: boolean;
}

const BUCKET = `remittance-files-${import.meta.env.VITE_ACCOUNT_ID}`;

const getLineDetails = (line: RemittanceLine): string => {
  if (!line.raw) return '';
  if (line.recordCode === 'M') return line.raw.slice(14, 94).trim();
  if (line.recordCode === 'T') return line.raw.slice(53, 63).trim();
  if (line.recordCode === '60') return line.raw.slice(21, 95).trim();
  return '';
};

export const RemittanceRowActions: FC<RemittanceRowActionsProps> = ({
  row,
  canModify,
}) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { data: details, isLoading: detailsLoading } = useGetRemittance({
    id: row.original.id,
    enabled: detailsOpen,
  });

  const viewFile = async (type: 'remittance' | 'p4') => {
    const key =
      type === 'remittance'
        ? row.original.filename
        : row.original.incomeFilePath;

    if (!key) {
      return;
    }

    setFileUrl(null);
    setModalOpen(true);

    try {
      setModalOpen(true);
      if (key) {
        const url = await generateGetSignedUrl({
          bucket: BUCKET,
          key: key,
        });
        setFileUrl(url);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const closePreview = () => {
    setModalOpen(false);
    setFileUrl(null);
  };

  const downloadRemittance = useDownloadFromS3(
    BUCKET,
    row.original.filename,
    setLoading
  );
  const downloadP4 = useDownloadFromS3(
    BUCKET,
    row.original.incomeFilePath,
    setLoading
  );

  return (
    <>
      <Menu shadow="md" width={220} withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="transparent"
            aria-label={translate('pages:remittances.actions.menuAriaLabel')}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <IconDotsVertical stroke={1.5} size={22} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>
            {translate('pages:remittances.actions.downloadsLabel')}
          </Menu.Label>
          <Menu.Item
            leftSection={<IconDownload size={14} />}
            disabled={!row.original.filename || !canModify || loading}
            onClick={() => downloadRemittance()}
          >
            {loading
              ? translate('pages:remittances.actions.downloading')
              : translate('pages:remittances.actions.remittance')}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconListDetails size={14} />}
            onClick={() => setDetailsOpen(true)}
          >
            {translate('pages:remittances.actions.details')}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconDownload size={14} />}
            disabled={!row.original.incomeFilePath || loading}
            onClick={() => downloadP4()}
          >
            {loading
              ? translate('pages:remittances.actions.downloading')
              : translate('pages:remittances.actions.p4')}
          </Menu.Item>
          <Menu.Label>
            {translate('pages:remittances.actions.previewLabel')}
          </Menu.Label>
          <Menu.Item
            leftSection={<IconEye size={14} />}
            disabled={!row.original.filename || !canModify || loading}
            onClick={() => viewFile('remittance')}
          >
            {loading
              ? translate('pages:remittances.actions.loading')
              : translate('pages:remittances.actions.remittance')}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconEye size={14} />}
            disabled={!row.original.incomeFilePath || loading}
            onClick={() => viewFile('p4')}
          >
            {loading
              ? translate('pages:remittances.actions.loading')
              : translate('pages:remittances.actions.p4')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <Modal
        opened={modalOpen}
        onClose={closePreview}
        size="xl"
        centered
        closeOnEscape
        closeOnClickOutside
        style={{ borderRadius: 10 }}
      >
        {fileUrl ? (
          <div
            style={{
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <AspectRatio ratio={3 / 3} style={{ width: '100%' }}>
              <iframe
                src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                title={translate('pages:remittances.actions.previewTitle')}
                style={{
                  flex: 1,
                  border: 'none',
                  padding: '2%',
                }}
              />
            </AspectRatio>
          </div>
        ) : (
          <Loader />
        )}
      </Modal>
      <Modal
        opened={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={translate('pages:remittances.details.title')}
        size="90%"
        centered
      >
        {detailsLoading ? (
          <Loader />
        ) : details?.remittanceLines?.length ? (
          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    {translate('pages:remittances.details.status')}
                  </Table.Th>
                  <Table.Th>
                    {translate('pages:remittances.details.claimNumber')}
                  </Table.Th>
                  <Table.Th>
                    {translate('pages:remittances.details.serviceDate')}
                  </Table.Th>
                  <Table.Th>
                    {translate('pages:remittances.details.tariff')}
                  </Table.Th>
                  <Table.Th>
                    {translate('pages:remittances.details.submitted')}
                  </Table.Th>
                  <Table.Th>
                    {translate('pages:remittances.details.paid')}
                  </Table.Th>
                  <Table.Th>
                    {translate('pages:remittances.details.eobCodes')}
                  </Table.Th>
                  <Table.Th>
                    {translate('pages:remittances.details.details')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {details.remittanceLines.map((line) => (
                  <Table.Tr key={line.id}>
                    <Table.Td>
                      {translate(
                        `pages:remittances.details.statusValues.${line.lineType}`,
                        line.lineType
                      )}
                    </Table.Td>
                    <Table.Td>
                      {line.claimNumber ||
                        translate('pages:remittances.details.notAvailable')}
                    </Table.Td>
                    <Table.Td>
                      {line.serviceDate ||
                        translate('pages:remittances.details.notAvailable')}
                    </Table.Td>
                    <Table.Td>
                      {line.tariffCode ||
                        translate('pages:remittances.details.notAvailable')}
                    </Table.Td>
                    <Table.Td>
                      {line.feeSubmitted ??
                        translate('pages:remittances.details.notAvailable')}
                    </Table.Td>
                    <Table.Td>{line.amountAssessed}</Table.Td>
                    <Table.Td>
                      {line.eobCodes?.join(', ') ||
                        translate('pages:remittances.details.notAvailable')}
                    </Table.Td>
                    <Table.Td>
                      {getLineDetails(line) ||
                        translate('pages:remittances.details.notAvailable')}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        ) : (
          <Text>{translate('pages:remittances.details.empty')}</Text>
        )}
      </Modal>
    </>
  );
};
